import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { CTABlock } from "@/components/CTABlock";
import { company } from "@/data/site";

export const Route = createFileRoute("/sms-compliance")({
  head: () => ({
    meta: [
      { title: "SMS Compliance & Text Messaging Policy | McKay Roofing" },
      { name: "description", content: "How McKay Roofing & Solar handles text messaging: consent, message frequency, opt-out instructions, rates and privacy." },
      { name: "keywords", content: "SMS compliance, text messaging policy, opt out, A2P 10DLC, McKay Roofing & Solar" },
      { property: "og:title", content: "SMS Compliance – McKay Roofing & Solar" },
      { property: "og:description", content: "Our text messaging consent, frequency and opt-out policy." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://mckay.steadyautogrowthdemo.com/sms-compliance" },
    ],
    links: [{ rel: "canonical", href: "https://mckay.steadyautogrowthdemo.com/sms-compliance" }],
  }),
  component: SMSPage,
});

function SMSPage() {
  const updated = "July 2026";
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        <section className="bg-primary text-primary-foreground">
          <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
            <h1 className="text-5xl font-extrabold text-balance sm:text-6xl">SMS Compliance</h1>
            <p className="mt-4 text-primary-foreground/80">Last updated: {updated}</p>
          </div>
        </section>

        <section className="prose prose-slate mx-auto max-w-3xl px-4 py-14 sm:px-6 lg:px-8">
          <h2 className="mt-4 text-2xl font-bold">Consent to receive text messages</h2>
          <p>
            When you give us your mobile number on a form, over the phone, or in person, you can
            choose to receive text messages from McKay Roofing &amp; Solar about your estimate,
            scheduling, crew arrival windows and job updates. Consent is never a condition of
            purchase, and we do not sell or share your mobile number with third parties for their
            own marketing.
          </p>

          <h2 className="mt-8 text-2xl font-bold">Types of messages we send</h2>
          <ul className="list-disc space-y-1 pl-6">
            <li>Appointment confirmations, reminders and arrival-window updates.</li>
            <li>Replies from our office team about your quote or project.</li>
            <li>Occasional service reminders, such as an annual roof check.</li>
          </ul>

          <h2 className="mt-8 text-2xl font-bold">Message frequency</h2>
          <p>
            Frequency varies with your project. Most customers get only a handful of messages while
            a job is active, and very few between projects.
          </p>

          <h2 className="mt-8 text-2xl font-bold">Message and data rates</h2>
          <p>
            Message and data rates may apply, depending on your mobile plan. We do not charge you
            anything to send or receive texts from us.
          </p>

          <h2 className="mt-8 text-2xl font-bold">How to opt out</h2>
          <p>
            Reply STOP to any message and we will stop texting that number. You will get one
            confirmation message, then nothing further. You can also call or email us and we will
            remove your number by hand. Opting out of texts does not cancel your project or stop us
            from reaching you by phone or email when we need to.
          </p>

          <h2 className="mt-8 text-2xl font-bold">How to get help</h2>
          <p>
            Reply HELP to any message, or reach our office directly:
          </p>
          <p>
            <a href={company.phoneHref} className="text-primary underline">{company.phone}</a>
            <br />
            <a href={`mailto:${company.email}`} className="text-primary underline">{company.email}</a>
          </p>

          <h2 className="mt-8 text-2xl font-bold">Carrier liability</h2>
          <p>
            Wireless carriers are not liable for delayed or undelivered messages. Delivery depends
            on your carrier and coverage.
          </p>

          <h2 className="mt-8 text-2xl font-bold">Privacy</h2>
          <p>
            Mobile numbers collected for SMS are handled under our Privacy Policy. We keep them only
            as long as needed to serve you and meet our record-keeping obligations.
          </p>
        </section>
        <CTABlock title="Ready for a free roofing or solar quote?" subtitle="Prefer a call or text? We'll reach out the way you choose." />
      </main>
      <SiteFooter />
    </div>
  );
}